// Contexte d'authentification React

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api';
import { API_CONFIG } from '../config/api.config';
import { AuthContextType, RegisterResult, User } from '../types/ComponentTypes'; // <-- retiré LoginResponse
import { initPushForUser } from '../services/pushService';
import axios from 'axios';
import { emitUnauthorized } from '../utils/authEvents';

// Type local pour la réponse de login
type LoginResponse = {
  token?: string;
  accessToken?: string;
  refreshToken?: string;
  user: User;
};

// Création du contexte
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Interface pour le provider
interface AuthProviderProps {
  children: ReactNode;
}

// Provider d'authentification
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fonction pour récupérer le token depuis le stockage local
  const getToken = () => localStorage.getItem('accessToken');

  const handleUnauthorizedLogout = () => {
    authAPI.logout();
    setUser(null);
    setToken(null);
  };

  // Initialiser l'authentification au chargement
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = getToken();
      
      if (storedToken) {
        setToken(storedToken);
        try {
          // ✅ CORRECTION : Utiliser la même logique que reinitializeAuth
          const response = await authAPI.getProfile();
          if (response.success && response.user) {
            setUser(response.user);
          } else {
            // Si le profil n'est pas valide, on déconnecte proprement
            console.error('Token invalide ou profil non trouvé, déconnexion.');
            authAPI.logout();
            setToken(null);
          }
        } catch (error) {
          console.error('Erreur lors de la vérification du token:', error);
          authAPI.logout();
          setToken(null);
        }
      }
      
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // ✅ Intercepteur global Axios pour détecter un 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        if (status === 401) {
          emitUnauthorized({ source: 'axios' });
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // ✅ Écouteur global : déconnexion si session expirée
  useEffect(() => {
    const handler = () => {
      handleUnauthorizedLogout();
    };
    window.addEventListener('auth:unauthorized', handler);
    return () => window.removeEventListener('auth:unauthorized', handler);
  }, []);

  // Fonction de connexion
  const login = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; message?: string; requiresEmailVerification?: boolean }> => {
    setIsLoading(true);
    try {
      const res: any = await authAPI.login(email, password);

      const accessToken = res.accessToken || res.token || null;
      const refreshToken = res.refreshToken || null;

      setUser(res.user || null);

      if (accessToken) {
        setToken(accessToken);
        localStorage.setItem('accessToken', accessToken);
      }

      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }

      return { success: true };
    } catch (e: any) {
      const msg = e?.message || 'Erreur de connexion';
      if (e?.status === 403) {
        return { success: false, message: msg, requiresEmailVerification: true };
      }
      return { success: false, message: msg };
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction d'inscription
  const register = async (data: {
    email: string;
    password: string;
    firstName: string;
    lastName?: string;
  }): Promise<RegisterResult> => {
    try {
      const res: any = await authAPI.register(
        data.email,
        data.password,
        data.firstName,
        data.lastName || ''
      );
      if (res.requiresEmailVerification) {
        return {
          success: true,
          requiresEmailVerification: true,
          message: res.message || 'Un email de vérification a été envoyé.'
        };
      }
      if (res.accessToken && res.user) {
        setUser(res.user);
        setToken(res.accessToken);
        return { success: true, message: 'Inscription réussie.' };
      }
      return { success: false, message: 'Réponse inattendue.' };
    } catch (e: any) {
      return { success: false, message: e?.message || 'Erreur inscription.' };
    }
  };

  // Méthode pour mettre à jour le profil
  const updateProfile = async (data: any): Promise<{ success: boolean; user: User }> => {
  try {
    const response = await authAPI.updateProfile(data);
    
    // ✅ Vérifier le flag success
    if (!response.success) {
      throw new Error('Échec de la mise à jour du profil');
    }
    
    setUser(response.user);
    return response; // ✅ Retourner la réponse complète
  } catch (error) {
    console.error('❌ Erreur updateProfile:', error);
    throw error;
  }
};

  // Méthode pour changer le mot de passe
  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      await authAPI.changePassword(currentPassword, newPassword);
    } catch (error) {
      throw error;
    }
  };

  // Méthode pour rafraîchir le profil
  const refreshProfile = async () => {
  try {
    const response = await authAPI.getProfile();

    if (response.success && response.user) {
      console.log('🔍 Profil rafraîchi:', response.user);
      setUser(response.user);
    } else {
      throw new Error('Échec de la récupération du profil');
    }
  } catch (error) {
    console.error('❌ Erreur refreshProfile:', error);
    throw error;
  }
};

  // ✅ AJOUTER : Fonctions pour le mot de passe oublié
  const forgotPassword = async (email: string): Promise<{ success: boolean; message: string }> => {
    // La logique est dans api.ts, on ne fait que l'appeler
    return authAPI.forgotPassword(email);
  };

  const resetPassword = async (token: string, password: string): Promise<{ success: boolean; message: string }> => {
    return authAPI.resetPassword(token, password);
  };

  // Fonction de déconnexion
  const logout = () => {
    handleUnauthorizedLogout();
  };

  // Fonction pour réinitialiser l'authentification (utile après Google OAuth)
  const reinitializeAuth = async () => {
    setIsLoading(true);
    const storedToken = getToken();
    
    if (storedToken) {
      setToken(storedToken);
      try {
        const response = await authAPI.getProfile();

        // ✅ AJOUTER CETTE LIGNE DE DÉBOGAGE
        console.log('🔍 RÉPONSE BRUTE DE L\'API getProfile :', response);

        if (response.success && response.user) {
          setUser(response.user);
        } else {
          throw new Error('Profil invalide ou non trouvé dans la réponse');
        }
      } catch (error) {
        console.error('Erreur lors de la réinitialisation:', error);
        authAPI.logout();
        setToken(null);
      }
    }
    
    setIsLoading(false);
  };

  // Fonction de connexion avec Google
  const googleLogin = async (): Promise<void> => {
    try {
      const baseUrl = API_CONFIG.getBaseUrl();
      const width = 500;
      const height = 600;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const popup = window.open(
        `${baseUrl}/auth/google`,
        'google-auth',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );
      if (!popup) return;

      const handler = async (event: MessageEvent) => {
        if (event.origin !== baseUrl) return;
        if (event.data?.type === 'GOOGLE_AUTH_SUCCESS') {
          const { accessToken, refreshToken, user } = event.data.payload || {};
          if (accessToken && user) {
            localStorage.setItem('accessToken', accessToken);
            if (refreshToken) {
              localStorage.setItem('refreshToken', refreshToken);
            }
            setToken(accessToken);
            setUser(user);
          } else {
            await reinitializeAuth?.();
          }
          window.removeEventListener('message', handler);
          popup.close();
        } else if (event.data?.type === 'GOOGLE_AUTH_ERROR') {
          console.error('Google auth error:', event.data.error);
          window.removeEventListener('message', handler);
          popup.close();
        }
      };
      window.addEventListener('message', handler);
    } catch (e) {
      console.error('googleLogin error', e);
    }
  };

  // Gestion du succès de la connexion
  const handleLoginSuccess = React.useCallback(async (payload: LoginResponse) => {
    const accessToken = payload.accessToken || payload.token || null;
    const refreshToken = payload.refreshToken || null;

    if (accessToken) {
      setToken(accessToken);
      localStorage.setItem('accessToken', accessToken);
    }

    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }

    setUser(payload.user);
    if (accessToken) {
      await initPushForUser(payload.user.id, accessToken);
    }
  }, []);

  // Valeur du contexte
  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    isLoading,
    updateProfile,
    changePassword,
    refreshProfile,
    forgotPassword,
    resetPassword,
    reinitializeAuth,
    googleLogin, // <-- AJOUT
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans AuthProvider');
  return ctx;
};
